import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  Zap, 
  TrendingUp, 
  Users, 
  Calendar,
  Mail,
  Star,
  ChevronRight,
  Menu,
  X,
  Globe,
  Sparkles,
  HelpCircle,
  Check,
  Bot,
  Search,
  ClipboardCheck,
  ChevronDown,
  Clock,
  Target,
  Send,
  Linkedin,
  FileSearch,
  BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { toast } from "sonner";
import { API } from "../App";

// Translations
const translations = {
  en: {
    nav: {
      services: "Services",
      products: "Products",
      about: "About",
      testimonials: "Testimonials",
      contact: "Contact",
      bookCall: "Book a Call"
    },
    hero: {
      badge: "AI-Powered Business Solutions",
      title: "Transform Your Business With",
      titleHighlight: "Intelligent Automation",
      description: "We build custom AI solutions for sales and customer success that help businesses scale revenue efficiently and intelligently.",
      cta: "Schedule Discovery Call",
      explore: "Explore Services",
      efficiency: "Avg. Efficiency Gain"
    },
    stats: {
      projects: "Projects Delivered",
      satisfaction: "Client Satisfaction",
      timeSaved: "Avg. Time Saved/Week",
      roi: "Average ROI"
    },
    services: {
      subtitle: "What We Do",
      title: "Our Services",
      description: "We specialize in sales and customer success, driving business transformation through artificial intelligence.",
      learnMore: "Learn more",
      watchWorkflow: "Watch the Workflow",
      howItWorks: "How it Works",
      items: [
        {
          id: "consultancy",
          title: "AI Consultancy",
          description: "Strategic guidance to help you navigate the AI landscape. We assess your business, identify opportunities, and create a roadmap for AI adoption.",
          hasVideo: false
        },
        {
          id: "linkedin",
          title: "LinkedIn Sales",
          description: "Automated outreach system that fills your pipeline with qualified leads.",
          howItWorks: ["Scraping Emails", "Building Database", "Getting Contacts", "Outreach"],
          hasVideo: true
        },
        {
          id: "poly",
          title: "Poly (Customer Success)",
          description: "AI-powered customer support that handles inquiries intelligently and efficiently.",
          howItWorks: ["Receiving Inquiries", "Classifying Issues", "Automated Resolution/Escalation", "Database Update"],
          hasVideo: true
        }
      ]
    },
    pricing: {
      subtitle: "Pricing & Partnerships",
      title: "Transparent Investment Models",
      structure: "Structure: Implementation Fee + Monthly Subscription",
      tiers: [
        {
          title: "Custom Projects",
          price: "Starting at $2,000 USD",
          description: "Tailored solutions built specifically for your business needs with full implementation support."
        },
        {
          title: "Ready-Made Solutions",
          price: "$300 USD/month",
          tag: "No Implementation Fee",
          description: "Pre-built AI solutions ready to deploy immediately into your workflow."
        }
      ]
    },
    caseStudy: {
      subtitle: "Case Study",
      title: "Totto AI: From Curiosity to AI-Driven Decisions",
      challenge: {
        title: "The Challenge",
        text: "The client had robust systems (ERP, POS) but lacked integrated intelligence. AI efforts were isolated experiments without executive ownership."
      },
      northStar: {
        title: "The North Star",
        text: "Goal: To become cost-efficient and intelligent in growth. Using AI to guide inventory planning, demand forecasting, and commercial decisions."
      },
      roadmap: {
        title: "The Strategic Roadmap",
        phases: [
          {
            phase: "Phase 1",
            title: "Foundations & Governance",
            description: "Focus on data maturity diagnosis and defining the 3-5 priority business cases with measurable impact."
          },
          {
            phase: "Phase 2",
            title: "Platform & Core Cases",
            description: "Launching the 'Executive Intelligence Layer', implementing Demand Forecasting, Inventory Optimization, and Smart CRM."
          },
          {
            phase: "Phase 3",
            title: "Scale & Competitive Advantage",
            description: "Regional expansion, Dynamic Pricing, and Advanced Personalization."
          }
        ]
      }
    },
    integrations: {
      subtitle: "Integrations",
      title: "Works With Your Existing Tools",
      description: "We integrate with your existing ecosystem. No need for new platforms.",
      platforms: ["HubSpot", "Salesforce", "LinkedIn", "WhatsApp", "Social Media"]
    },
    about: {
      subtitle: "About Us",
      title: "Building the Future of Business Intelligence",
      p1: "We're a team of AI engineers, sales automation specialists, and customer success strategists dedicated to helping companies leverage the power of artificial intelligence.",
      p2: "Our approach combines cutting-edge technology with deep expertise in sales and customer success, ensuring every solution we deliver creates measurable revenue impact.",
      tags: {
        expert: "Expert Team",
        fast: "Fast Delivery",
        quality: "Quality Assured"
      }
    },
    products: {
      subtitle: "Our Products",
      title: "AI-Powered Solutions Built for Scale",
      description: "Discover our suite of intelligent automation products designed to supercharge your sales and customer success operations.",
      clickToExplore: "Click to explore",
      items: [
        {
          id: "follow-up",
          title: "Smart Follow-up System",
          tagline: "Never Miss a Follow-up Again",
          description: "An intelligent Email follow-up agent that analyzes client profiles on ClickUp and sends hyper-personalized follow-ups based on your desired criteria.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/jm3vi3gu_Email_followup_english.mp4",
          features: [
            "Checks ClickUp tasks daily at your scheduled time",
            "Analyzes tasks based on email threads and meeting context",
            "Crafts hyper-personalized follow-up emails",
            "Automatically sends emails to clients on your behalf"
          ]
        },
        {
          id: "linkedin",
          title: "Research & LinkedIn Outreach Agent",
          tagline: "Autonomous Lead Generation",
          description: "A fully autonomous agent that researches leads, scrapes websites for intel, and engages prospects on LinkedIn with precision.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/frt6apo6_Linkedin_English.mp4",
          features: [
            "Define your ideal lead profile once",
            "Scours the internet, Google Maps & websites for leads",
            "Filters and adds qualified prospects to your sheet",
            "LinkedIn agent engages on their profiles automatically"
          ]
        },
        {
          id: "auditor",
          title: "AI Support Ticket Auditor",
          tagline: "100% QA Coverage, Zero Manual Effort",
          description: "Automate your QA process by auditing every support ticket. Grades interactions against your quality matrix for Empathy, Resolution & Grammar.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/tid7q15e_AI_scoring_english.mp4",
          features: [
            "Receive email prompts to add tickets to the sheet",
            "System samples tickets based on your defined criteria",
            "AI grades resolved tickets against quality matrix",
            "Get final email reports with employee scores"
          ]
        }
      ]
    },
    testimonials: {
      subtitle: "Testimonials",
      title: "What Our Clients Say",
      items: [
        {
          name: "Sarah Chen",
          content: "The AI solutions transformed our sales pipeline. We saw a 40% increase in qualified leads within 2 months."
        },
        {
          name: "Marcus Johnson",
          content: "Their automation workflows saved us 40+ hours per week. The ROI was immediate and substantial."
        },
        {
          name: "Adriana Alarcon Torres",
          role: "Politecnico Grancolombiano Colombia",
          content: "We saved significant time from manually scoring our CS team. The AI grades them accurately and consistently."
        }
      ]
    },
    cta: {
      title: "Ready to Transform Your Business?",
      description: "Book a free 30-minute discovery call to discuss how AI can revolutionize your operations.",
      button: "Schedule Your Discovery Call"
    },
    faq: {
      subtitle: "FAQ",
      title: "Frequently Asked Questions",
      items: [
        {
          q: "Can we start implementing AI solutions right away?",
          a: "We believe in building on solid foundations. Before deploying any AI or automation, we conduct a complimentary Sales & Success Audit to map your current workflows. This ensures every solution we implement directly addresses your bottlenecks and delivers measurable ROI from day one."
        },
        {
          q: "What does a partnership engagement include?",
          a: "A Yantra AI partnership is an ongoing collaboration focused on continuously optimizing your sales and customer success operations. With predictable monthly investment, you get dedicated support, regular strategy sessions, and priority access to new AI capabilities as they emerge."
        },
        {
          q: "How does project-based pricing work?",
          a: "For defined initiatives like building an AI-powered lead scoring system or automating customer onboarding, we scope everything upfront. You'll know the exact investment, deliverables, and timeline before we begin. Payments are milestone-based, tied to tangible outcomes. Our pricing for pre-established systems starts at $2,000, with a $300 monthly maintenance cost."
        },
        {
          q: "Is there a minimum commitment for partnerships?",
          a: "Our partnerships run for an initial 6-month period. This allows proper implementation, team training, and performance measurement. After the initial term, you have full flexibility with just 30 days notice. We earn your continued business through results, not contracts."
        },
        {
          q: "What tools or platforms do we need to get started?",
          a: "There is no need to have platforms specific, but we do have integrations with HubSpot, with Salesforce, with LinkedIn, with WhatsApp, with, um, social media."
        },
        {
          q: "What's your billing structure?",
          a: "Complete transparency: partnerships are billed monthly in advance, while projects follow milestone-based payments. No hidden fees, no surprises. We provide detailed invoices aligned with your finance team's requirements."
        }
      ]
    },
    contact: {
      subtitle: "Contact",
      title: "Get in Touch",
      description: "Have a question or want to learn more? Reach out to us directly.",
      emailLabel: "Email us at",
      form: {
        title: "Send us a message",
        name: "Your Name",
        email: "Your Email",
        message: "Your Message",
        submit: "Send Message"
      }
    },
    footer: {
      rights: "© 2025 Yantra AI. All rights reserved."
    }
  },
  es: {
    nav: {
      services: "Servicios",
      products: "Productos",
      about: "Nosotros",
      testimonials: "Testimonios",
      contact: "Contacto",
      bookCall: "Agendar Llamada"
    },
    hero: {
      badge: "Soluciones Empresariales con IA",
      title: "Transforma Tu Negocio Con",
      titleHighlight: "Automatización Inteligente",
      description: "Construimos soluciones de IA personalizadas para ventas y éxito del cliente que ayudan a las empresas a escalar ingresos de manera eficiente e inteligente.",
      cta: "Agenda tu Llamada",
      explore: "Explorar Servicios",
      efficiency: "Ganancia Promedio de Eficiencia"
    },
    stats: {
      projects: "Proyectos Entregados",
      satisfaction: "Satisfacción del Cliente",
      timeSaved: "Tiempo Ahorrado/Semana",
      roi: "ROI Promedio"
    },
    services: {
      subtitle: "Lo Que Hacemos",
      title: "Nuestros Servicios",
      description: "Nos especializamos en ventas y éxito del cliente, impulsando la transformación empresarial a través de la inteligencia artificial.",
      learnMore: "Saber más",
      watchWorkflow: "Ver el Flujo de Trabajo",
      howItWorks: "Cómo Funciona",
      items: [
        {
          id: "consultancy",
          title: "Consultoría de IA",
          description: "Orientación estratégica para navegar el panorama de IA. Evaluamos tu negocio, identificamos oportunidades y creamos una hoja de ruta para la adopción de IA.",
          hasVideo: false
        },
        {
          id: "linkedin",
          title: "LinkedIn Sales",
          description: "Sistema de alcance automatizado que llena tu pipeline con leads calificados.",
          howItWorks: ["Extracción de Emails", "Construcción de Base de Datos", "Obtención de Contactos", "Alcance"],
          hasVideo: true
        },
        {
          id: "poly",
          title: "Poly (Éxito del Cliente)",
          description: "Soporte al cliente impulsado por IA que maneja consultas de manera inteligente y eficiente.",
          howItWorks: ["Recepción de Consultas", "Clasificación de Problemas", "Resolución/Escalamiento Automático", "Actualización de Base de Datos"],
          hasVideo: true
        }
      ]
    },
    pricing: {
      subtitle: "Precios y Asociaciones",
      title: "Modelos de Inversión Transparentes",
      structure: "Estructura: Tarifa de Implementación + Suscripción Mensual",
      tiers: [
        {
          title: "Proyectos Personalizados",
          price: "Desde $2,000 USD",
          description: "Soluciones a medida construidas específicamente para las necesidades de tu negocio con soporte completo de implementación."
        },
        {
          title: "Soluciones Listas",
          price: "$300 USD/mes",
          tag: "Sin Tarifa de Implementación",
          description: "Soluciones de IA pre-construidas listas para implementar inmediatamente en tu flujo de trabajo."
        }
      ]
    },
    caseStudy: {
      subtitle: "Caso de Estudio",
      title: "Totto AI: De la Curiosidad a Decisiones Impulsadas por IA",
      challenge: {
        title: "El Desafío",
        text: "El cliente tenía sistemas robustos (ERP, POS) pero carecía de inteligencia integrada. Los esfuerzos de IA eran experimentos aislados sin propiedad ejecutiva."
      },
      northStar: {
        title: "El Norte",
        text: "Objetivo: Ser eficiente en costos e inteligente en crecimiento. Usar IA para guiar la planificación de inventario, pronóstico de demanda y decisiones comerciales."
      },
      roadmap: {
        title: "La Hoja de Ruta Estratégica",
        phases: [
          {
            phase: "Fase 1",
            title: "Fundamentos y Gobernanza",
            description: "Enfoque en diagnóstico de madurez de datos y definición de los 3-5 casos de negocio prioritarios con impacto medible."
          },
          {
            phase: "Fase 2",
            title: "Plataforma y Casos Core",
            description: "Lanzamiento de la 'Capa de Inteligencia Ejecutiva', implementando Pronóstico de Demanda, Optimización de Inventario y CRM Inteligente."
          },
          {
            phase: "Fase 3",
            title: "Escala y Ventaja Competitiva",
            description: "Expansión regional, Precios Dinámicos y Personalización Avanzada."
          }
        ]
      }
    },
    integrations: {
      subtitle: "Integraciones",
      title: "Funciona Con Tus Herramientas Existentes",
      description: "Nos integramos con tu ecosistema existente. No necesitas nuevas plataformas.",
      platforms: ["HubSpot", "Salesforce", "LinkedIn", "WhatsApp", "Redes Sociales"]
    },
    about: {
      subtitle: "Nosotros",
      title: "Construyendo el Futuro de la Inteligencia Empresarial",
      p1: "Somos un equipo de ingenieros de IA, especialistas en automatización de ventas y estrategas de éxito del cliente dedicados a ayudar a las empresas a aprovechar el poder de la inteligencia artificial.",
      p2: "Nuestro enfoque combina tecnología de vanguardia con profunda experiencia en ventas y éxito del cliente, asegurando que cada solución que entregamos genere un impacto medible en los ingresos.",
      tags: {
        expert: "Equipo Experto",
        fast: "Entrega Rápida",
        quality: "Calidad Garantizada"
      }
    },
    products: {
      subtitle: "Nuestros Productos",
      title: "Soluciones de IA Diseñadas para Escalar",
      description: "Descubre nuestra suite de productos de automatización inteligente diseñados para potenciar tus operaciones de ventas y éxito del cliente.",
      clickToExplore: "Clic para explorar",
      items: [
        {
          id: "follow-up",
          title: "Sistema de Seguimiento Inteligente",
          tagline: "Nunca Pierdas un Seguimiento",
          description: "Un agente inteligente de seguimiento por email que analiza perfiles de clientes en ClickUp y envía seguimientos hiperpersonalizados según tus criterios.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/npwxardl_Email_followup_spanish.mp4",
          features: [
            "Revisa tareas de ClickUp diariamente a tu hora programada",
            "Analiza tareas basándose en hilos de email y contexto de reuniones",
            "Crea emails de seguimiento hiperpersonalizados",
            "Envía automáticamente emails a clientes en tu nombre"
          ]
        },
        {
          id: "linkedin",
          title: "Agente de Investigación y LinkedIn",
          tagline: "Generación de Leads Autónoma",
          description: "Un agente completamente autónomo que investiga leads, extrae información de sitios web y contacta prospectos en LinkedIn con precisión.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/hqsen0ip_Linkedin_Spanish.mp4",
          features: [
            "Define tu perfil de lead ideal una vez",
            "Busca en internet, Google Maps y sitios web por leads",
            "Filtra y agrega prospectos calificados a tu hoja",
            "Agente de LinkedIn interactúa en sus perfiles automáticamente"
          ]
        },
        {
          id: "auditor",
          title: "Auditor de Tickets con IA",
          tagline: "100% Cobertura QA, Cero Esfuerzo Manual",
          description: "Automatiza tu proceso QA auditando cada ticket de soporte. Califica interacciones contra tu matriz de calidad para Empatía, Resolución y Gramática.",
          videoUrl: "https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/xh9dx3q7_AI_scoring_spanish.mp4",
          features: [
            "Recibe emails para agregar tickets a la hoja",
            "Sistema muestrea tickets según tus criterios definidos",
            "IA califica tickets resueltos contra matriz de calidad",
            "Recibe reportes finales por email con puntuaciones"
          ]
        }
      ]
    },
    testimonials: {
      subtitle: "Testimonios",
      title: "Lo Que Dicen Nuestros Clientes",
      items: [
        {
          name: "Sarah Chen",
          content: "Las soluciones de IA transformaron nuestro pipeline de ventas. Vimos un aumento del 40% en leads calificados en 2 meses."
        },
        {
          name: "Marcus Johnson",
          content: "Sus flujos de automatización nos ahorraron más de 40 horas por semana. El ROI fue inmediato y sustancial."
        },
        {
          name: "Adriana Alarcon Torres",
          role: "Politecnico Grancolombiano Colombia",
          content: "Ahorramos tiempo significativo al dejar de calificar manualmente a nuestro equipo de CS. La IA los califica de manera precisa y consistente."
        }
      ]
    },
    cta: {
      title: "¿Listo para Transformar Tu Negocio?",
      description: "Agenda una llamada de descubrimiento gratuita de 30 minutos para discutir cómo la IA puede revolucionar tus operaciones.",
      button: "Agenda Tu Llamada"
    },
    faq: {
      subtitle: "PREGUNTAS FRECUENTES",
      title: "Preguntas Frecuentes",
      items: [
        {
          q: "¿Podemos comenzar a implementar soluciones de IA de inmediato?",
          a: "Creemos en construir sobre bases sólidas. Antes de implementar cualquier IA o automatización, realizamos una Auditoría de Ventas y Éxito gratuita para mapear tus flujos actuales. Esto asegura que cada solución aborde directamente tus cuellos de botella y entregue ROI medible desde el primer día."
        },
        {
          q: "¿Qué incluye un compromiso de partnership?",
          a: "Un partnership con Yantra AI es una colaboración continua enfocada en optimizar constantemente tus operaciones de ventas y éxito del cliente. Con una inversión mensual predecible, obtienes soporte dedicado, sesiones de estrategia regulares y acceso prioritario a nuevas capacidades de IA."
        },
        {
          q: "¿Cómo funciona el precio por proyecto?",
          a: "Para iniciativas definidas como construir un sistema de puntuación de leads con IA o automatizar el onboarding de clientes, definimos todo por adelantado. Sabrás la inversión exacta, entregables y cronograma antes de comenzar. Los pagos están basados en hitos, vinculados a resultados tangibles."
        },
        {
          q: "¿Hay un compromiso mínimo para partnerships?",
          a: "Nuestros partnerships tienen un período inicial de 6 meses. Esto permite una implementación adecuada, capacitación del equipo y medición de rendimiento. Después del término inicial, tienes flexibilidad total con solo 30 días de aviso. Nos ganamos tu negocio continuo a través de resultados, no contratos."
        },
        {
          q: "¿Qué herramientas o plataformas necesitamos para comenzar?",
          a: "Trabajamos con plataformas líderes como HubSpot, Salesforce, Zapier y Make. Si aún no las usas, no te preocupes. Nuestro equipo maneja todo el proceso de configuración e integración, asegurando una transición fluida sin interrupciones a tus operaciones."
        },
        {
          q: "¿Cuál es su estructura de facturación?",
          a: "Transparencia completa: los partnerships se facturan mensualmente por adelantado, mientras que los proyectos siguen pagos basados en hitos. Sin tarifas ocultas, sin sorpresas. Proporcionamos facturas detalladas alineadas con los requisitos de tu equipo de finanzas."
        }
      ]
    },
    contact: {
      subtitle: "Contacto",
      title: "Contáctanos",
      description: "¿Tienes alguna pregunta o quieres saber más? Comunícate directamente con nosotros.",
      emailLabel: "Escríbenos a",
      form: {
        title: "Envíanos un mensaje",
        name: "Tu Nombre",
        email: "Tu Email",
        message: "Tu Mensaje",
        submit: "Enviar Mensaje"
      }
    },
    footer: {
      rights: "© 2025 Yantra AI. Todos los derechos reservados."
    }
  }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [contactLoading, setContactLoading] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: "" });
  const t = translations[lang];

  const productIcons = {
    "follow-up": Mail,
    "linkedin": Linkedin,
    "auditor": ClipboardCheck
  };

  const productGradients = {
    "follow-up": "from-orange-500 to-red-500",
    "linkedin": "from-blue-500 to-cyan-500",
    "auditor": "from-purple-500 to-pink-500"
  };

  const serviceIcons = {
    "consultancy": Sparkles,
    "linkedin": Linkedin,
    "poly": Bot
  };

  const stats = [
    { value: "100+", label: t.stats.projects },
    { value: "95%", label: t.stats.satisfaction },
    { value: "70hrs", label: t.stats.timeSaved },
    { value: "9.8x", label: t.stats.roi }
  ];

  const toggleLanguage = () => {
    setLang(lang === "en" ? "es" : "en");
  };

  const handleBookCall = () => {
    window.open("https://calendly.com/coally/coally-30-min", "_blank");
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error(lang === "en" ? "Please fill in all fields" : "Por favor complete todos los campos");
      return;
    }
    
    // Open mailto link for static hosting
    const subject = encodeURIComponent(`Contact from ${contactForm.name}`);
    const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`);
    window.location.href = `mailto:tanaygawade2000@gmail.com?subject=${subject}&body=${body}`;
    
    toast.success(lang === "en" ? "Opening your email client..." : "Abriendo tu cliente de email...");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex flex-col items-start" data-testid="nav-logo">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-2xl text-[#FF4F00]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Yantra
                </span>
                <span className="font-bold text-2xl text-[#1A1A1A]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  AI
                </span>
              </div>
              <span className="text-[10px] text-[#64748B] tracking-wide">powered by Coally</span>
            </a>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#products" className="nav-link" data-testid="nav-products">{t.nav.products}</a>
              <a href="#about" className="nav-link" data-testid="nav-about">{t.nav.about}</a>
              <a href="#contact" className="nav-link" data-testid="nav-contact">{t.nav.contact}</a>
            </div>
            
            <div className="hidden md:flex items-center space-x-4">
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 hover:border-[#FF4F00] transition-colors"
                data-testid="language-toggle"
              >
                <Globe className="w-4 h-4 text-[#64748B]" />
                <span className="text-sm font-medium text-[#1A1A1A]">{lang.toUpperCase()}</span>
              </button>
              <Button 
                className="btn-primary"
                onClick={handleBookCall}
                data-testid="nav-book-btn"
              >
                {t.nav.bookCall}
              </Button>
            </div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-gray-100 py-4 px-6"
          >
            <div className="flex flex-col space-y-4">
              <a href="#products" className="nav-link py-2">{t.nav.products}</a>
              <a href="#about" className="nav-link py-2">{t.nav.about}</a>
              <a href="#contact" className="nav-link py-2">{t.nav.contact}</a>
              <hr className="my-2" />
              <button
                onClick={toggleLanguage}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-gray-200"
              >
                <Globe className="w-4 h-4 text-[#64748B]" />
                <span className="text-sm font-medium">{lang === "en" ? "Español" : "English"}</span>
              </button>
              <Button className="btn-primary w-full" onClick={handleBookCall}>
                {t.nav.bookCall}
              </Button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden bg-grid">
        <div className="absolute inset-0 bg-mesh pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center px-4 py-2 bg-[#FF4F00]/10 rounded-full mb-6">
                <span className="text-sm font-medium text-[#FF4F00]">{t.hero.badge}</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] leading-tight mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
                data-testid="hero-title"
              >
                {t.hero.title}{" "}
                <span className="text-[#FF4F00]">{t.hero.titleHighlight}</span>
              </h1>
              <p className="text-lg text-[#64748B] mb-8 max-w-xl">
                {t.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  className="btn-primary inline-flex items-center gap-2"
                  onClick={handleBookCall}
                  data-testid="hero-cta-btn"
                >
                  {t.hero.cta}
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline"
                  className="btn-secondary"
                  onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-services-btn"
                >
                  {t.hero.explore}
                </Button>
              </div>
            </motion.div>
            
            <motion.div 
              className="lg:col-span-5 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden bg-[#1A1A1A]">
                <video 
                  src="https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/mspl9wtg_Screen-Now-20251225T153601.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="hero-video"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
              </div>
              {/* Floating stats card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1A1A1A]">+80%</p>
                    <p className="text-sm text-[#64748B]">{t.hero.efficiency}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-[#94A3B8] text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.about.subtitle}</span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                {t.about.title}
              </h2>
              <p className="text-[#64748B] mb-6">
                {t.about.p1}
              </p>
              <p className="text-[#64748B] mb-8">
                {t.about.p2}
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                  <Users className="w-5 h-5 text-[#FF4F00]" />
                  <span className="text-sm font-medium">{t.about.tags.expert}</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                  <Zap className="w-5 h-5 text-[#FF4F00]" />
                  <span className="text-sm font-medium">{t.about.tags.fast}</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg">
                  <Star className="w-5 h-5 text-[#FF4F00]" />
                  <span className="text-sm font-medium">{t.about.tags.quality}</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#1A1A1A]">
                <video 
                  src="https://customer-assets.emergentagent.com/job_bookbot-agency/artifacts/5z00tmyi_Screen-Now-20251225T154624.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  data-testid="about-video"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="section-padding bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.products.subtitle}</span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="products-title"
            >
              {t.products.title}
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              {t.products.description}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {t.products.items.map((product, index) => {
              const Icon = productIcons[product.id];
              const isExpanded = expandedProduct === product.id;
              
              return (
                <motion.div
                  key={product.id}
                  className={`relative cursor-pointer ${isExpanded ? 'md:col-span-3' : ''}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.2,
                    ease: "easeOut"
                  }}
                  data-testid={`product-${product.id}`}
                  onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                >
                  <motion.div 
                    className={`relative bg-[#1A1A1A] rounded-3xl overflow-hidden ${isExpanded ? 'p-8 md:p-12' : 'p-8'}`}
                    whileHover={{ scale: isExpanded ? 1 : 1.02 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${productGradients[product.id]} opacity-0 hover:opacity-10 transition-opacity duration-500`} />
                    
                    <div className={`relative z-10 ${isExpanded ? 'grid md:grid-cols-2 gap-8 items-start' : ''}`}>
                      {/* Card Header */}
                      <div>
                        {/* Icon */}
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${productGradients[product.id]} flex items-center justify-center mb-6`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        
                        {/* Title & Tagline */}
                        <h3 
                          className="text-xl md:text-2xl font-bold text-white mb-2"
                          style={{ fontFamily: 'Syne, sans-serif' }}
                        >
                          {product.title}
                        </h3>
                        
                        <p className="text-[#FF4F00] font-medium text-sm mb-4">
                          {product.tagline}
                        </p>
                        
                        {/* Description */}
                        <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
                          {product.description}
                        </p>
                        
                        {/* Video Demo Button */}
                        {!isExpanded && (
                          <div className="mb-6 rounded-xl overflow-hidden border border-white/10">
                            {product.videoUrl ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVideoModal({ isOpen: true, videoUrl: product.videoUrl });
                                }}
                                className="w-full p-6 bg-gradient-to-br from-white/10 to-white/5 hover:from-white/15 hover:to-white/10 transition-all duration-300 group/demo"
                              >
                                <div className="flex flex-col items-center gap-3">
                                  <div className="w-16 h-16 bg-[#FF4F00] rounded-full flex items-center justify-center group-hover/demo:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4F00]/30">
                                    <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </div>
                                  <span className="text-white font-semibold text-sm uppercase tracking-wider">
                                    {lang === "en" ? "Watch Demo" : "Ver Demo"}
                                  </span>
                                </div>
                              </button>
                            ) : (
                              <div className="p-4 bg-white/5 text-center">
                                <div className="w-12 h-12 bg-[#FF4F00]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <svg className="w-6 h-6 text-[#FF4F00]" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                                <p className="text-sm text-white font-medium">{lang === "en" ? "Video Coming Soon" : "Video Próximamente"}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Click indicator */}
                        {!isExpanded && (
                          <div className="flex items-center gap-2 text-[#64748B] text-sm hover:text-[#FF4F00] transition-colors">
                            <span>{t.products.clickToExplore}</span>
                            <ChevronDown className="w-4 h-4" />
                          </div>
                        )}
                      </div>
                      
                      {/* Expanded Features */}
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: 0.1 }}
                          className="space-y-4"
                        >
                          <h4 className="text-white font-semibold text-lg mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                            {lang === "en" ? "How It Works" : "Cómo Funciona"}
                          </h4>
                          {product.features.map((feature, fIndex) => (
                            <motion.div
                              key={fIndex}
                              className="flex items-start gap-4"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 + fIndex * 0.1 }}
                            >
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FF4F00]/20 flex items-center justify-center mt-0.5">
                                <span className="text-[#FF4F00] font-bold text-sm">{fIndex + 1}</span>
                              </div>
                              <p className="text-[#E2E8F0] text-sm leading-relaxed">{feature}</p>
                            </motion.div>
                          ))}
                          
                          {/* Video Demo Button in expanded view */}
                          <motion.div 
                            className="mt-6 rounded-xl overflow-hidden border border-white/10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                          >
                            {product.videoUrl ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setVideoModal({ isOpen: true, videoUrl: product.videoUrl });
                                }}
                                className="w-full p-8 bg-gradient-to-br from-white/10 to-white/5 hover:from-white/15 hover:to-white/10 transition-all duration-300 group/demo"
                              >
                                <div className="flex flex-col items-center gap-4">
                                  <div className="w-20 h-20 bg-[#FF4F00] rounded-full flex items-center justify-center group-hover/demo:scale-110 transition-transform duration-300 shadow-lg shadow-[#FF4F00]/30">
                                    <svg className="w-9 h-9 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </div>
                                  <span className="text-white font-semibold uppercase tracking-wider">
                                    {lang === "en" ? "Watch Full Demo" : "Ver Demo Completo"}
                                  </span>
                                </div>
                              </button>
                            ) : (
                              <div className="p-6 bg-white/5 text-center">
                                <div className="w-16 h-16 bg-[#FF4F00]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                                  <svg className="w-8 h-8 text-[#FF4F00]" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z"/>
                                  </svg>
                                </div>
                                <p className="text-white font-medium mb-1">{lang === "en" ? "Watch Demo Video" : "Ver Video Demo"}</p>
                                <p className="text-[#64748B] text-sm">{lang === "en" ? "Coming Soon" : "Próximamente"}</p>
                              </div>
                            )}
                          </motion.div>
                          
                          <motion.div 
                            className="pt-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                          >
                            <Button 
                              className="bg-[#FF4F00] hover:bg-[#E64600] text-white rounded-full px-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBookCall();
                              }}
                            >
                              {lang === "en" ? "Get Started" : "Comenzar"}
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </motion.div>
                        </motion.div>
                      )}
                    </div>
                    
                    {/* Close button when expanded */}
                    {isExpanded && (
                      <motion.button
                        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedProduct(null);
                        }}
                      >
                        <X className="w-4 h-4" />
                      </motion.button>
                    )}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.pricing.subtitle}</span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="pricing-title"
            >
              {t.pricing.title}
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto text-lg">
              {t.pricing.structure}
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {t.pricing.tiers.map((tier, index) => (
              <motion.div
                key={index}
                className={`relative p-8 rounded-3xl border-2 ${index === 1 ? 'border-[#FF4F00] bg-[#FF4F00]/5' : 'border-gray-200 bg-white'} hover:shadow-xl transition-all duration-300`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`pricing-tier-${index}`}
              >
                {tier.tag && (
                  <span className="absolute -top-3 left-8 bg-[#FF4F00] text-white text-xs font-bold px-4 py-1 rounded-full">
                    {tier.tag}
                  </span>
                )}
                <h3 className="text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {tier.title}
                </h3>
                <p className="text-3xl font-bold text-[#FF4F00] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {tier.price}
                </p>
                <p className="text-[#64748B]">{tier.description}</p>
                <Button 
                  className={`mt-6 w-full rounded-full ${index === 1 ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={handleBookCall}
                >
                  {lang === "en" ? "Get Started" : "Comenzar"}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="section-padding bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.integrations.subtitle}</span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="integrations-title"
            >
              {t.integrations.title}
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              {t.integrations.description}
            </p>
          </motion.div>
          
          <div className="flex flex-wrap justify-center gap-6">
            {t.integrations.platforms.map((platform, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl px-8 py-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-[#FF4F00]/30 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`integration-${platform.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF4F00]/10 rounded-lg flex items-center justify-center">
                    {platform === "HubSpot" && <span className="text-[#FF4F00] font-bold text-lg">H</span>}
                    {platform === "Salesforce" && <span className="text-[#FF4F00] font-bold text-lg">S</span>}
                    {platform === "LinkedIn" && <Linkedin className="w-5 h-5 text-[#FF4F00]" />}
                    {platform === "WhatsApp" && <span className="text-[#FF4F00] font-bold text-lg">W</span>}
                    {(platform === "Social Media" || platform === "Redes Sociales") && <Globe className="w-5 h-5 text-[#FF4F00]" />}
                  </div>
                  <span className="font-semibold text-[#1A1A1A]">{platform}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.testimonials.subtitle}</span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="testimonials-title"
            >
              {t.testimonials.title}
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {t.testimonials.items.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#FF4F00] text-[#FF4F00]" />
                  ))}
                </div>
                <p className="text-[#1A1A1A] mb-6">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#FF4F00] rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {testimonial.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">{testimonial.name}</p>
                    {testimonial.role && (
                      <p className="text-sm text-[#64748B]">{testimonial.role}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1A1A1A] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              {t.cta.title}
            </h2>
            <p className="text-[#94A3B8] mb-8 max-w-2xl mx-auto">
              {t.cta.description}
            </p>
            <Button 
              className="btn-primary inline-flex items-center gap-2"
              onClick={handleBookCall}
              data-testid="cta-book-btn"
            >
              <Calendar className="w-5 h-5" />
              {t.cta.button}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.faq.subtitle}</span>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="faq-title"
            >
              {t.faq.title}
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="w-full">
              {t.faq.items.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-b border-gray-200"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-[#1A1A1A] font-semibold hover:text-[#FF4F00] hover:no-underline py-6 text-base">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#64748B] pb-6 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FF4F00] font-medium text-sm uppercase tracking-wider">{t.contact.subtitle}</span>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-4 mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                {t.contact.title}
              </h2>
              <p className="text-[#64748B] mb-8">
                {t.contact.description}
              </p>
              
              <div className="space-y-6">
                <a 
                  href="mailto:tanaygawade2000@gmail.com"
                  className="flex items-center gap-4 group"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-[#FF4F00]/10 rounded-xl flex items-center justify-center group-hover:bg-[#FF4F00] transition-colors">
                    <Mail className="w-5 h-5 text-[#FF4F00] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#64748B]">{t.contact.emailLabel}</p>
                    <p className="font-medium text-[#1A1A1A]">tanaygawade2000@gmail.com</p>
                  </div>
                </a>
              </div>
            </motion.div>
            
            <motion.div
              className="bg-[#F8F9FA] rounded-3xl p-8"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold text-[#1A1A1A] mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                {t.contact.form.title}
              </h3>
              <form className="space-y-4" onSubmit={handleContactSubmit} data-testid="contact-form">
                <div>
                  <input 
                    type="text" 
                    placeholder={t.contact.form.name}
                    className="input-field"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <input 
                    type="email" 
                    placeholder={t.contact.form.email}
                    className="input-field"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <textarea 
                    placeholder={t.contact.form.message}
                    rows={4}
                    className="input-field resize-none py-3"
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="btn-primary w-full"
                  disabled={contactLoading}
                  data-testid="contact-submit-btn"
                >
                  {contactLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {lang === "en" ? "Sending..." : "Enviando..."}
                    </span>
                  ) : (
                    t.contact.form.submit
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center md:items-start">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-2xl text-[#FF4F00]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Yantra
                </span>
                <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                  AI
                </span>
              </div>
              <span className="text-[10px] text-[#64748B] tracking-wide">powered by Coally</span>
            </div>
            <p className="text-[#64748B] text-sm">
              {t.footer.rights}
            </p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence>
        {videoModal.isOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideoModal({ isOpen: false, videoUrl: "" })}
          >
            {/* Backdrop */}
            <motion.div 
              className="absolute inset-0 bg-black/90 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Video Container */}
            <motion.div
              className="relative w-full max-w-5xl aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src={videoModal.videoUrl}
                autoPlay
                controls
                className="w-full h-full object-contain"
              />
              
              {/* Close Button */}
              <button
                onClick={() => setVideoModal({ isOpen: false, videoUrl: "" })}
                className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;
